// Use localhost for development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/** Same-tab auth sync (StorageEvent only fires across tabs). */
export function notifyAuthStorageChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('nls-auth-changed'));
  }
}

export const otpService = {
  // Send OTP to phone
  async sendOTP(phone: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Send OTP error:', error);
      throw new Error(error.message || 'Failed to send OTP. Please try again.');
    }
  },

  // Verify OTP
  async verifyOTP(phone: string, otp: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, otp }),
      });

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Verify OTP error:', error);
      throw new Error(error.message || 'Failed to verify OTP');
    }
  },

  // Resend OTP
  async resendOTP(phone: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/resend-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to resend OTP');
      }

      return data;
    } catch (error: any) {
      console.error('Resend OTP error:', error);
      throw new Error(error.message || 'Failed to resend OTP. Please try again.');
    }
  },

  // Login with phone and OTP
  async phoneLogin(phone: string, otp: string, firebaseIdToken?: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/phone-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone,
          otp,
          ...(firebaseIdToken ? { firebaseIdToken } : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if (data.token) {
        const isAdmin = data.user?.role === 'admin';
        if (isAdmin) {
          localStorage.setItem('adminToken', data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.user));
        } else {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        notifyAuthStorageChanged();
      }

      return data;
    } catch (error: any) {
      console.error('[otpService] Phone login error:', error);
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  },

  /** Shopper login only — rejects staff (admin) accounts. */
  async phoneLoginCustomerOnly(phone: string, otp: string, firebaseIdToken?: string) {
    try {
      const response = await fetch(`${API_BASE_URL}/otp/phone-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone,
          otp,
          ...(firebaseIdToken ? { firebaseIdToken } : {}),
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }
      if (data.user?.role === 'admin') {
        throw new Error(
          'This sign-in is for customers only. Staff accounts use the separate staff sign-in page.'
        );
      }
      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        notifyAuthStorageChanged();
      }
      return data;
    } catch (error: any) {
      console.error('[otpService] Customer phone login error:', error);
      throw new Error(error.message || 'Login failed. Please try again.');
    }
  },
};

export const authService = {
  // Register new user
  async register(userData: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.error || `Registration failed (${response.status})`);
      }

      if (data.token) {
        const isAdmin = data.user?.role === 'admin';
        if (isAdmin) {
          localStorage.setItem('adminToken', data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.user));
        } else {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
      }
      return data;
    } catch (error: any) {
      console.error('Register error:', error);
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  // Login user (supports admin or customer — used by staff flows)
  async login(credentials: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `Login failed (${response.status})`);
      }

      if (data.token) {
        console.log('[authService] Saving token and user to localStorage');
        const isAdmin = data.user?.role === 'admin';
        if (isAdmin) {
          localStorage.setItem('adminToken', data.token);
          localStorage.setItem('adminUser', JSON.stringify(data.user));
        } else {
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        console.log('[authService] Saved successfully');
      }
      return data;
    } catch (error: any) {
      console.error('[authService] Login error:', error);
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  /** Customer /login page only — rejects admin accounts (no staff session stored here). */
  async loginCustomerOnly(credentials: { email: string; password: string }) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || data.error || `Login failed (${response.status})`);
      }
      if (data.user?.role === 'admin') {
        throw new Error(
          'This page is for shoppers only. Staff sign-in uses a separate link — please use that.'
        );
      }
      if (data.token && data.user) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        notifyAuthStorageChanged();
      }
      return data;
    } catch (error: any) {
      console.error('[authService] Customer login error:', error);
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  /** Shopper / customer session only — does not clear admin panel session */
  async logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('cart');
    notifyAuthStorageChanged();
  },

  /** Shopper profile only (`user` key). Never returns admin panel identity — use getCurrentAdmin(). */
  getCurrentUser() {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        return null;
      }
      const user = JSON.parse(userStr);
      return user;
    } catch (error) {
      console.error('[authService] Error parsing user from localStorage', error);
      return null;
    }
  },

  /** Shopper session only (for cart, checkout, wishlist). */
  isCustomerAuthenticated() {
    const userToken = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (!userToken || !userStr) return false;
    try {
      const user = JSON.parse(userStr);
      return user?.role !== 'admin';
    } catch {
      return false;
    }
  },

  // Legacy: customer OR admin token present
  isAuthenticated() {
    return this.isCustomerAuthenticated() || this.isAdminAuthenticated();
  },

  /** Customer JWT only. Admin API calls must use getAdminToken(). */
  getToken() {
    return localStorage.getItem('token');
  },

  // Verify current shopper session (uses `token` only)
  async verifySession() {
    const token = this.getToken();
    if (!token) {
      throw new Error('No token found');
    }

    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearAuthData();
        notifyAuthStorageChanged();
      }
      throw new Error('Session expired. Please sign in again.');
    }

    const data = await response.json();
    return data.user;
  },

  /** Admin panel session — uses `adminToken` only (never shopper `token`). */
  async verifyAdminSession() {
    const token = this.getAdminToken();
    if (!token) {
      throw new Error('No token found');
    }

    const response = await fetch(`${API_BASE_URL}/auth/verify`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.clearAdminAuthData();
        notifyAuthStorageChanged();
      }
      throw new Error('Session expired. Please sign in again.');
    }

    const data = await response.json();
    return data.user;
  },

  // Clear authentication data
  clearAuthData() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Save authentication data (customer registration / login)
  saveAuthData(token: string, user: any) {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    notifyAuthStorageChanged();
  },

  // Admin login - uses separate storage
  async adminLogin(credentials: any) {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(credentials),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || data.error || `Login failed (${response.status})`);
      }

      if (data.token && data.user?.role === 'admin') {
        this.saveAdminAuthData(data.token, data.user, data.sessionId);
      }
      return data;
    } catch (error: any) {
      console.error('[authService] adminLogin error:', error);
      throw new Error(error.message || 'Network error. Please check your connection.');
    }
  },

  async adminPhoneLogin(phone: string, firebaseIdToken: string) {
    const response = await fetch(`${API_BASE_URL}/auth/admin-phone-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone, firebaseIdToken }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || `Login failed (${response.status})`);
    }
    if (data.token && data.user?.role === 'admin') {
      this.saveAdminAuthData(data.token, data.user, data.sessionId);
    }
    return data;
  },

  // Admin logout - clears only admin storage
  async adminLogout() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminSessionId');
  },

  // Get current admin user
  getCurrentAdmin() {
    try {
      const userStr = localStorage.getItem('adminUser');
      if (!userStr) {
        return null;
      }
      return JSON.parse(userStr);
    } catch (error) {
      return null;
    }
  },

  // Check if admin is authenticated
  isAdminAuthenticated() {
    const token = localStorage.getItem('adminToken');
    return !!token;
  },

  // Get admin auth token
  getAdminToken() {
    return localStorage.getItem('adminToken');
  },

  // Clear admin authentication data
  clearAdminAuthData() {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('adminSessionId');
  },

  // Save admin authentication data
  saveAdminAuthData(token: string, user: any, sessionId?: string) {
    localStorage.setItem('adminToken', token);
    localStorage.setItem('adminUser', JSON.stringify(user));
    if (sessionId) {
      localStorage.setItem('adminSessionId', sessionId);
    }
    notifyAuthStorageChanged();
  },
};
